import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/auth';
import { api } from '../../api/axios';
import { Navigate, useNavigate, useLocation } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './Login.module.css';
import logoIcon from "assets/logo.png";
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import { loginSchema, LoginFormValues } from './authSchemas';
import { SubmitHandler, Controller } from 'react-hook-form';
import Loading from 'components/Loading/Loading';
import SearchableSelect from 'components/SearchableSelect/SearchableSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import slideShowImage1 from "assets/slideshow/1.jpg";
import slideShowImage2 from "assets/slideshow/2.jpg";
import slideShowImage3 from "assets/slideshow/3.jpg";
import slideShowImage4 from "assets/slideshow/4.png";
import slideShowImage5 from "assets/slideshow/5.jpg";
import slideShowImage6 from "assets/slideshow/6.jpg";
import slideShowImage7 from "assets/slideshow/7.jpg";

const SLIDE_IMAGES = [
    slideShowImage1,
    slideShowImage2,
    slideShowImage3,
    slideShowImage4,
    slideShowImage5,
    slideShowImage6,
    slideShowImage7
];

const Login = () => {
    const { login, isAuthLoading, isAuthenticated } = useAuth()!;
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const location = useLocation();
    const isSuperAdmin = location.pathname === '/admin-login';
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema) as any,
        defaultValues: {
            agreeTerms: true,
            rememberMe: false,
            school_id: ''
        }
    });

    const [schools, setSchools] = useState<any[]>([]);
    const [isLoadingSchools, setIsLoadingSchools] = useState(true);
    const identifier = watch('identifier');

    useEffect(() => {
        setValue('is_super_admin', isSuperAdmin);
        if (isSuperAdmin) {
            setValue('school_id', '');
        }
    }, [isSuperAdmin, setValue]);

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const res = await api.get('/schools/names');
                // Ensure we get an array of schools
                const schoolData = res.data.data || res.data;
                setSchools(schoolData.map((s: any) => ({
                    id: s.institutional_id,
                    name: s.name,
                    code: s.school_id || s.unique_code
                })));
            } catch (err) {
                console.error('Failed to fetch schools', err);
            } finally {
                setIsLoadingSchools(false);
            }
        };
        fetchSchools();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % SLIDE_IMAGES.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    if (isAuthLoading) {
        return <Loading fullPage message="Authenticating..." />;
    }

    if (isAuthenticated) {
        return <Navigate to='/' replace />;
    }

    const onSubmit: SubmitHandler<LoginFormValues> = async (data) => {
        const payloadKey = isSuperAdmin ? 'email' : (data.identifier.includes('@') ? 'email' : 'username');
        const payload: any = {
            [payloadKey]: data.identifier,
            password: data.password
        };

        if (isSuperAdmin) {
            payload.is_super_admin = true;
        } else if (data.school_id) {
            payload.school_id = data.school_id;
        }

        try {
            const res = await api.post('/auth/login', payload);
            login(res.data.data.accessToken, res.data.data.user);
            navigate('/', { replace: true });
        } catch (err: any) {
            console.error('Login Error', err);
            alert(err.response?.data?.message || 'Login Failed');
        }
    };

    return (
        <div className={styles.loginContainer}>
            {SLIDE_IMAGES.map((img, index) => (
                <img
                    key={`bg-${img}`}
                    src={img}
                    alt=""
                    className={`${styles.pageBackgroundImage} ${index === currentImageIndex ? styles.active : ''}`}
                />
            ))}
            <div className={styles.loginBackgroundOverlay} />
            
            <div className={styles.loginBox}>
                <div className={styles.leftSection}>
                    <div className={styles.logoContainer}>
                        <img src={logoIcon} alt="Sensible-VR Logo" className={styles.logoImage} />
                    </div>

                    <div className={styles.subHeading}>
                        {isSuperAdmin ? "Super Admin Portal. Please login." : "Welcome back. Please login to your account."}
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>

                        {!isSuperAdmin && (
                            <div className={styles.formGroup}>
                                <Controller
                                    name="school_id"
                                    control={control}
                                    rules={{ required: !isSuperAdmin ? 'Please select a school' : false }}
                                    render={({ field }) => (
                                        <SearchableSelect
                                            label="Select School"
                                            options={schools}
                                            placeholder="Choose your institution"
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.school_id?.message}
                                            hideSearchIcon={true}
                                            dropdownClassName={styles.dropdownMenu}
                                            inputClassName={styles.input}
                                            labelClassName={styles.label}
                                        />
                                    )}
                                />
                            </div>
                        )}

                        <InputWithLabel
                            label={isSuperAdmin ? "Email" : "Username or Email"}
                            type="text"
                            containerClassName={styles.formGroup}
                            labelClassName={styles.label}
                            className={styles.input}
                            placeholder={isSuperAdmin ? "e.g. admin@sensible-vr.com" : "e.g. johndoe or user@school.com"}
                            error={errors.identifier?.message}
                            {...register('identifier')}
                        />


                        <InputWithLabel
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            containerClassName={styles.formGroup}
                            labelClassName={styles.label}
                            className={styles.input}
                            placeholder="Enter password"
                            error={errors.password?.message}
                            iconRight={
                                <FontAwesomeIcon 
                                    icon={showPassword ? faEyeSlash : faEye} 
                                    style={{ cursor: 'pointer', color: 'rgba(255, 255, 255, 0.4)' }}
                                    onClick={() => setShowPassword(!showPassword)}
                                />
                            }
                            {...register('password')}
                        />

                        <div className={styles.utilityRow}>
                            <label className={styles.checkboxContainer}>
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    {...register('rememberMe')}
                                />
                                Remember Me
                            </label>
                            <a href="#" className={styles.forgotPassword}>Forgot Password?</a>
                        </div>

                        <Button
                            type="submit"
                            className={styles.signInButton}
                            fullWidth
                            isLoading={isSubmitting}
                        >
                            Sign In
                        </Button>

                        <div className={styles.termsContainer}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    {...register('agreeTerms')}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span>
                                        By logging in, you confirm that you have read and agree to our <a href="https://sensible-vr.com/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="https://sensible-vr.com/privacy.html" target="_blank" rel="noopener noreferrer">Cookies Policy</a>.
                                    </span>
                                    {errors.agreeTerms && (
                                        <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                            {errors.agreeTerms.message}
                                        </span>
                                    )}
                                </div>
                            </label>
                        </div>
                    </form>
                </div>

                <div className={styles.rightSection}>
                    <svg className={styles.capIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                    </svg>

                    <div className={styles.rightOverlay}>
                        <div className={styles.pill}>EDUCATIONAL PLATFORM</div>
                        <h2 className={styles.rightHeading}>Empowering the next generation of learners.</h2>
                        <p className={styles.rightSubText}>Access your personalized dashboard, interactive courses, and virtual campus resources.</p>
                    </div>

                    {SLIDE_IMAGES.map((img, index) => (
                        <img
                            key={img}
                            src={img}
                            alt={`VR Slide ${index}`}
                            className={`${styles.slideshowImage} ${index === currentImageIndex ? styles.active : ''}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Login;
